import React, { Suspense, lazy, useEffect, useState } from 'react';
import { getCurrentUser, signInWithHostedUi, signOut } from './auth.v6';
import { getLocalApiMode, localApiTokenStorageKey, readLocalApiToken, shouldBypassHostedAuth } from './utils/localDev';

const Home = lazy(() => import('./pages/Home.jsx'));

function DevAuthBar({ mode, onTokenSaved }) {
  const [token, setToken] = useState(readLocalApiToken());

  const handleTokenSave = () => {
    const trimmedToken = token.trim();
    if (trimmedToken) {
      window.localStorage.setItem(localApiTokenStorageKey, trimmedToken);
    } else {
      window.localStorage.removeItem(localApiTokenStorageKey);
    }
    onTokenSaved();
  };

  const barStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 12,
    padding: '10px 12px',
    background: '#5f6a80',
    border: '1px solid #2a2f3a',
    borderRadius: 8
  };

  const labelStyle = {
    color: 'var(--text)',
    fontSize: 14,
    fontWeight: 600
  };

  const inputStyle = {
    minWidth: 260,
    background: '#202838',
    border: '1px solid #374151',
    color: 'var(--text)',
    padding: '7px 10px',
    borderRadius: 6
  };

  return (
    <div className="container" style={barStyle}>
      <div style={labelStyle}>Local development auth: {mode}</div>
      {mode === 'token' && (
        <input
          aria-label="Local API token"
          placeholder="API bearer token"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          style={inputStyle}
        />
      )}
      {mode === 'token' && (
        <button onClick={handleTokenSave}>Use token</button>
      )}
    </div>
  );
}

function AuthGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [tokenVersion, setTokenVersion] = useState(0);
  const localApiMode = getLocalApiMode();

  useEffect(() => {
    if (shouldBypassHostedAuth()) {
      setUser({ username: `local-${localApiMode}` });
      setChecking(false);
      return undefined;
    }

    let mounted = true;
    (async () => {
      const u = await getCurrentUser();
      if (!mounted) return;
      if (!u) {
        // Redirect to Hosted UI for login
        await signInWithHostedUi();
        return;
      }
      setUser(u);
      setChecking(false);
    })();
    return () => { mounted = false; };
  }, [localApiMode]);

  if (checking) return <div className="container">Authenticating...</div>;

  if (shouldBypassHostedAuth()) {
    return (
      <div>
        <DevAuthBar mode={localApiMode} onTokenSaved={() => setTokenVersion((version) => version + 1)} />
        <div key={`${localApiMode}-${tokenVersion}`}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>Signed in as {user?.username || user?.userId}</div>
        <button onClick={() => signOut()}>Sign out</button>
      </div>
      {children}
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<div className="container">Loading...</div>}>
      <AuthGate>
        <Home />
      </AuthGate>
    </Suspense>
  );
}
